/**
 * Observability stub.
 *
 * Single chokepoint for error/event reporting from the render bundle.
 * In dev it logs structured JSON to the console (`console.error` /
 * `console.info`, both whitelisted by the lint rule). In production it
 * is a no-op until Phase 11 wires Sentry + PostHog through this same
 * surface.
 *
 * Why a stub here in P1: every other module already routes through
 * `reportError` / `reportEvent`, so flipping the production transport
 * on later is a one-file change. No call sites need to know.
 */

type Context = Record<string, unknown> | undefined;

interface ImportMetaEnvLike {
  readonly DEV?: boolean;
  readonly MODE?: string;
}

function isDev(): boolean {
  // `import.meta.env.DEV` is `true` for `vite` + `vite preview`; `false`
  // for production builds. Bundler inlines it, so dead-code elim drops
  // the dev branch from the production bundle.
  const env = (import.meta.env ?? {}) as ImportMetaEnvLike;
  return env.DEV === true || env.MODE === 'development';
}

/** Report a thrown / caught error with optional structured context. */
export function reportError(err: Error, context?: Context): void {
  if (!isDev()) {
    // Phase 11: Sentry.captureException(err, { extra: context });
    return;
  }
  // eslint-disable-next-line no-console
  console.error(
    JSON.stringify({
      ts: new Date().toISOString(),
      level: 'error',
      name: err.name,
      message: err.message,
      stack: err.stack,
      ...(context ?? {}),
    }),
  );
}

/** Report a named event (page view, retry, fallback engaged, etc.). */
export function reportEvent(name: string, props?: Context): void {
  if (!isDev()) {
    // Phase 11: PostHog.capture(name, props);
    return;
  }
  // eslint-disable-next-line no-console
  console.info(
    JSON.stringify({
      ts: new Date().toISOString(),
      level: 'info',
      event: name,
      ...(props ?? {}),
    }),
  );
}
